import React from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Select from '@cloudscape-design/components/select';
import Input from '@cloudscape-design/components/input';
import DateRangePicker, {
  DateRangePickerProps,
} from '@cloudscape-design/components/date-range-picker';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Tiles from '@cloudscape-design/components/tiles';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';

import axios from 'axios';

import {
  runQuery,
  getResults,
  paramsKey,
  getCachedMappingsByQueryId,
  QueryRequest,
  ResultRow,
  Granularity,
  ViewType,
} from '../api/ForecastApi';
import { businessesForView, businesses, regionsForBusiness } from '../config/businesses';
import {
  DATE_PRESETS,
  DEFAULT_PRESET_DAYS,
  DEFAULT_RANGE,
  presetToRange,
  toDateStrings,
  isValidRange,
  DATE_RANGE_I18N,
} from '../utils/dateRange';
import { VIEWS, DEFAULT_VIEW_ID, getView } from '../views/registry';
import { ParamKey } from '../views/types';
import { parseFgids, parseSiteIds } from '../utils/parseInputs';
import { friendlyError } from '../utils/errors';
import { buildCsvFileName } from '../utils/csv';
import { resolveColumn, distinctGroups } from '../utils/groups';
import { computeDivergence, DivergenceResult } from '../utils/divergence';
import ResultsTable from './ResultsTable';
import ResultChart from './ResultChart';
import ExplainabilityView from './ExplainabilityView';
import DivergenceChart from './DivergenceChart';
import AgentExplanationInline from './AgentExplanationInline';
import AgentChat from './AgentChat';
import RecentRunsApiPanel from './RecentRunsApiPanel';
import { saveRecentRun, RecentRunItem } from '../api/recentRunsApi';
import './AnalysisView.css';

type Option = { label: string; value: string };

const toOptions = (values: string[]): Option[] =>
  values.map((v) => ({ label: v, value: v }));

// Demand rows carry fgid (add site_id); lifespan rows carry site_id (add fgid).
function enrichRows(
  data: ResultRow[],
  fgidSiteMapping: Record<string, string> = {},
  siteFgidMapping: Record<string, number> = {}
): ResultRow[] {
  return data.map((row) => {
    const enriched = { ...row };
    if (row.fgid != null && fgidSiteMapping[row.fgid] !== undefined) {
      enriched.site_id = fgidSiteMapping[row.fgid];
    }
    if (row.site_id != null && siteFgidMapping[row.site_id] !== undefined) {
      enriched.fgid = siteFgidMapping[row.site_id];
    }
    return enriched;
  });
}

export default function AnalysisView(): JSX.Element {
  const [viewId, setViewId] = React.useState<string>(DEFAULT_VIEW_ID);
  const [business, setBusiness] = React.useState<Option | null>(null);
  const [region, setRegion] = React.useState<Option | null>(null);
  const [dateRange, setDateRange] =
    React.useState<DateRangePickerProps.Value | null>(DEFAULT_RANGE);
  const [dateMode, setDateMode] = React.useState<number | 'custom'>(
    DEFAULT_PRESET_DAYS
  );
  const [fgidsInput, setFgidsInput] = React.useState('');
  const [siteIdsInput, setSiteIdsInput] = React.useState('');
  const [asinsInput, setAsinsInput] = React.useState('');
  const [targetDatesInput, setTargetDatesInput] = React.useState(
    new Date().toISOString().split('T')[0]
  );
  const [fcdRangeInput, setFcdRangeInput] = React.useState('');
  const [granularity, setGranularity] = React.useState<Granularity>('detailed');

  const [loading, setLoading] = React.useState(false);
  // Which view initiated the in-flight query, so loading UI is scoped to that view.
  const [loadingViewId, setLoadingViewId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<ResultRow[] | null>(null);
  const [csvFileName, setCsvFileName] = React.useState('forecast-results.csv');
  const [display, setDisplay] = React.useState<'chart' | 'table'>('chart');
  const [submitted, setSubmitted] = React.useState(false);
  const [divergenceResult, setDivergenceResult] = React.useState<DivergenceResult | null>(null);
  const [divRunsKey, setDivRunsKey] = React.useState(0);
  // Prefill payload passed to ExplainabilityView when the user clicks 'Investigate this FCD'
  // on a divergence metric point. Switches view without submitting so they can override.
  const [pendingExplainability, setPendingExplainability] = React.useState<
    { business?: string; region?: string; fg?: string; fcd?: string; asins?: string } | undefined
  >(undefined);

  const abortRef = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => abortRef.current?.abort(), []);

  // Live mirror of the active view so an in-flight query that completes after the user
  // switched views doesn't clobber the now-active view's shared display state.
  const viewIdRef = React.useRef(viewId);
  React.useEffect(() => { viewIdRef.current = viewId; }, [viewId]);

  const autoRunPendingRef = React.useRef(false);

  // Focus the range picker when Custom is selected.
  const pickerRef = React.useRef<DateRangePickerProps.Ref>(null);
  React.useEffect(() => {
    if (dateMode === 'custom') {
      pickerRef.current?.focus();
    }
  }, [dateMode]);

  // Editing any filter un-commits the current query, so Clear can reappear.
  React.useEffect(() => {
    setSubmitted(false);
  }, [business, region, fgidsInput, siteIdsInput, asinsInput, dateMode, dateRange]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Pre-fill the form from the readable params (present alongside queryId too)
    // so the user can see the query and use Refresh/Clear.
    const b = params.get('business');
    const r = params.get('region');
    const v = params.get('view') ?? params.get('viewType');
    const f = params.get('fgids');
    const s = params.get('siteIds');
    const sd = params.get('startDate');
    const ed = params.get('endDate');

    if (v) setViewId(v);
    if (b) setBusiness({ label: b, value: b });
    if (r) setRegion({ label: r, value: r });
    if (f) setFgidsInput(f);
    if (s) setSiteIdsInput(s);
    if (sd && ed) {
      setDateRange({ type: 'absolute', startDate: sd, endDate: ed });
      setDateMode('custom');
    }

    // queryId link: fetch stored results directly (skip the auto-run).
    const queryId = params.get('queryId');
    const urlAwsRegion = params.get('awsRegion');
    const urlViewType = params.get('viewType');
    if (queryId && urlAwsRegion && urlViewType) {
      setLoading(true);
      setLoadingViewId(v ?? viewId);
      getResults(queryId, urlAwsRegion, urlViewType as ViewType)
        .then((results) => {
          if (results.status === 'succeeded') {
            const mappings = results.fgidSiteMapping
              ? {
                  fgidSiteMapping: results.fgidSiteMapping,
                  siteFgidMapping: results.siteFgidMapping,
                }
              : getCachedMappingsByQueryId(queryId);
            const enrichedData = enrichRows(
              results.data ?? [],
              mappings?.fgidSiteMapping,
              mappings?.siteFgidMapping
            );
            setRows(enrichedData);
            if (urlViewType === 'divergence') {
              setDivergenceResult(computeDivergence(enrichedData));
            }
            setSubmitted(true);
          } else if (results.status === 'failed') {
            setError(
              results.error ||
                'Cached query results expired. Please re-run the query.'
            );
          } else {
            setError('Query results not available. Please re-run the query.');
          }
        })
        .catch((e) =>
          setError(e instanceof Error ? e.message : 'Failed to load results')
        )
        .finally(() => { setLoading(false); setLoadingViewId(null); });
      return;
    }

    if (b && r) autoRunPendingRef.current = true;
  }, []);

  const view = getView(viewId);
  // Loading indicator is only "true" for the view that started the query.
  const isViewLoading = loading && loadingViewId === viewId;
  const has = (p: ParamKey) => view.params.includes(p);

  const businessOptions = view.viewType
    ? businessesForView(view.viewType)
    : businesses();

  const handleViewChange = (newViewId: string) => {
    // Do NOT abort an in-flight query when switching views. The backend Athena query
    // runs to completion regardless, so cancelling only throws away a result we're
    // already paying for. Let it finish in the background and populate state; the
    // per-view result/AI panels are gated on view.viewType, so they simply hide while
    // you're on another view and reappear when you switch back.
    const newView = getView(newViewId);
    setViewId(newViewId);
    if (
      business &&
      newView.viewType &&
      !businessesForView(newView.viewType).includes(business.value)
    ) {
      setBusiness(null);
      setRegion(null);
    }
    setError(null);
  };

  const regionOptions = business ? toOptions(regionsForBusiness(business.value)) : [];
  const groupCount = React.useMemo(() => {
    if (!rows || !view.chart?.groupColumn) return 0;
    const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
    const col = resolveColumn(keys, view.chart.groupColumn, view.chart.groupMatch);
    return col ? distinctGroups(rows, col).length : 0;
  }, [rows, view.chart]);

  const applyPreset = (days: number) => {
    setDateMode(days);
    setDateRange(presetToRange(days));
  };

  const resolveDates = (): {
    startDate?: string;
    endDate?: string;
    error?: string;
  } => {
    const validation = isValidRange(dateRange);
    if (!validation.valid) {
      return { error: validation.errorMessage };
    }
    return toDateStrings(dateRange);
  };

  const onCancel = () => {
    abortRef.current?.abort();
    setLoading(false);
    setLoadingViewId(null);
    setSubmitted(false);
  };

  // True when any input differs from its default.
  const hasActiveFilters =
    Boolean(business) ||
    Boolean(region) ||
    fgidsInput.trim() !== '' ||
    siteIdsInput.trim() !== '' ||
    dateMode !== DEFAULT_PRESET_DAYS;

  const onClear = () => {
    setBusiness(null);
    setRegion(null);
    setFgidsInput('');
    setSiteIdsInput('');
    setAsinsInput('');
    setDateMode(DEFAULT_PRESET_DAYS);
    setDateRange(DEFAULT_RANGE);
    setError(null);
    window.history.replaceState({}, '', window.location.pathname);
  };

  // Builds the query request from the current form state, or returns a
  // validation error. Shared by Analyze and Refresh so the cache key matches.
  const buildRequest = (): { request?: QueryRequest; error?: string } => {
    if (!view.viewType) return {};
    if (has('business') && !business) return { error: 'Business is required' };
    if (has('region') && !region) return { error: 'Region is required' };

    const request: QueryRequest = {
      business: business?.value ?? '',
      region: region?.value ?? '',
      viewType: view.viewType,
    };
    if (has('fgids')) {
      const fgids = parseFgids(fgidsInput);
      if (fgids.length) request.fgids = fgids;
    }
    if (has('siteIds')) {
      const siteIds = parseSiteIds(siteIdsInput);
      if (siteIds.length) request.siteIds = siteIds;
    }
    if (has('asins') && asinsInput.trim()) {
      const asins = asinsInput.split(',').map((a) => a.trim()).filter(Boolean);
      if (asins.length) request.asins = asins;
    }
    if (has('dateRange')) {
      const dates = resolveDates();
      if (dates.error) return { error: dates.error };
      if (dates.startDate) request.startDate = dates.startDate;
      if (dates.endDate) request.endDate = dates.endDate;
    }
    request.granularity = granularity;
    return { request };
  };

  // Drops the cached queryId for the current params and re-runs from the API.
  const onRefresh = () => {
    const { request } = buildRequest();
    if (!request) return;
    try {
      localStorage.removeItem(paramsKey(request));
    } catch {
      // best-effort
    }
    void onSubmit();
  };

  const onSubmit = async () => {
    setError(null);
    const { request, error: buildError } = buildRequest();
    if (buildError) {
      setError(buildError);
      return;
    }
    if (!request) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const submitViewId = viewId;
    setLoading(true);
    setLoadingViewId(submitViewId);
    setSubmitted(true);
    setRows(null);
    setDivergenceResult(null);
    try {
      const {
        data,
        fgidSiteMapping,
        siteFgidMapping,
        queryId,
        awsRegion,
      } = await runQuery(request, controller.signal);
      const enrichedRows = enrichRows(data, fgidSiteMapping, siteFgidMapping);
      // If the user switched to a different view while this ran, don't overwrite the
      // now-active view's shared results (the divergence result below is view-gated and
      // safe to keep). We still finish the request so its result is cached server-side.
      const stillActive = viewIdRef.current === submitViewId;
      if (stillActive) {
        setRows(enrichedRows);
        setCsvFileName(buildCsvFileName(request));
      }

      // Compute divergence for the divergence view
      if (request.viewType === 'divergence') {
        const result = computeDivergence(enrichedRows);
        setDivergenceResult(result);
      }

      // Save a recent run (DDB, shared) for query-views that have a backend
      // recent-runs partition. view.recentRunsType is the typed link to that
      // partition — no cast, and views without one (explainability) are skipped.
      if (view.recentRunsType) {
        const startD = dateRange?.type === 'absolute' ? dateRange.startDate : '';
        const endD = dateRange?.type === 'absolute' ? dateRange.endDate : '';
        const labelParts = [business?.value, region?.value,
          fgidsInput ? `fg:${fgidsInput}` : '', asinsInput ? `asin:${asinsInput}` : '',
          siteIdsInput ? `site:${siteIdsInput}` : '',
          startD && endD ? `${startD}\u2192${endD}` : ''].filter(Boolean);
        saveRecentRun({
          view_type: view.recentRunsType,
          label: labelParts.join(' \u00b7 '),
          params: { business: business?.value ?? '', region: region?.value ?? '',
            fgids: fgidsInput, asins: asinsInput, siteIds: siteIdsInput,
            startDate: startD, endDate: endD, view: viewId },
          handle: { queryId, awsRegion },
        }).then(() => setDivRunsKey((k) => k + 1)).catch(() => { /* best-effort */ });
      }

      // queryId drives the fast results fetch; the readable params let the
      // shared link pre-fill the form.
      const params = new URLSearchParams();
      params.set('queryId', queryId);
      params.set('awsRegion', awsRegion);
      params.set('viewType', request.viewType);
      if (business) params.set('business', business.value);
      if (region) params.set('region', region.value);
      if (viewId) params.set('view', viewId);
      if (fgidsInput) params.set('fgids', fgidsInput);
      if (siteIdsInput) params.set('siteIds', siteIdsInput);
      if (dateRange?.type === 'absolute') {
        params.set('startDate', dateRange.startDate);
        params.set('endDate', dateRange.endDate);
      }
      if (stillActive) window.history.replaceState({}, '', `?${params.toString()}`);
    } catch (e) {
      if (controller.signal.aborted) return;
      // Un-commit on failure so the Clear button is available again.
      setSubmitted(false);
      const statusCode = axios.isAxiosError(e) ? e.response?.status : undefined;
      let message = 'Query failed';
      if (axios.isAxiosError<{ error?: string }>(e) && e.response?.data?.error) {
        message = e.response.data.error;
      } else if (e instanceof Error) {
        message = e.message;
      }
      setError(friendlyError(message, statusCode));
    } finally {
      if (!controller.signal.aborted) { setLoading(false); setLoadingViewId(null); }
    }
  };
  React.useEffect(() => {
    if (autoRunPendingRef.current && business && region) {
      autoRunPendingRef.current = false;
      void onSubmit();
    }
  }, [business, region]);

  // Re-query when the granularity toggles (aggregation is server-side now).
  React.useEffect(() => {
    if (rows && submitted) {
      void onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity]);

  // Load a saved divergence run. Repopulate the form for display, then re-fetch the
  // cached Athena results (fast, no re-analyze) when we have a queryId; fall back to a
  // live re-run only for older saved runs or if the cached query expired.
  const loadRecentRun = async (run: RecentRunItem) => {
    const p = (run.params ?? {}) as Record<string, string>;
    const h = (run.handle ?? {}) as Record<string, string>;
    if (p.view) setViewId(p.view);
    if (p.business) setBusiness({ label: p.business, value: p.business });
    if (p.region) setRegion({ label: p.region, value: p.region });
    setFgidsInput(p.fgids ?? '');
    setSiteIdsInput(p.siteIds ?? '');
    setAsinsInput(p.asins ?? '');
    if (p.startDate && p.endDate) {
      setDateRange({ type: 'absolute', startDate: p.startDate, endDate: p.endDate });
      setDateMode('custom');
    }

    const rvt = (p.view ?? run.view_type) as ViewType;
    if (h.queryId && h.awsRegion) {
      abortRef.current?.abort();
      setError(null);
      setLoading(true);
      setLoadingViewId(p.view ?? viewId);
      setSubmitted(true);
      setRows(null);
      setDivergenceResult(null);
      try {
        const results = await getResults(h.queryId, h.awsRegion, rvt);
        if (results.status === 'succeeded') {
          const mappings = results.fgidSiteMapping
            ? { fgidSiteMapping: results.fgidSiteMapping, siteFgidMapping: results.siteFgidMapping }
            : getCachedMappingsByQueryId(h.queryId);
          const enriched = enrichRows(results.data ?? [], mappings?.fgidSiteMapping, mappings?.siteFgidMapping);
          setRows(enriched);
          if (run.view_type === 'divergence') setDivergenceResult(computeDivergence(enriched));
        } else if (p.business && p.region) {
          autoRunPendingRef.current = true;
        } else {
          setError('Cached results expired. Adjust parameters and Analyze again.');
        }
      } catch {
        if (p.business && p.region) autoRunPendingRef.current = true;
      } finally {
        setLoading(false);
        setLoadingViewId(null);
      }
      return;
    }
    if (p.business && p.region) autoRunPendingRef.current = true;
  };

  // Investigate a divergent FCD in the Explainability view (pre-fill, no submit).
  const handleInvestigateFcd = (fcd: string) => {
    const firstFg = fgidsInput.split(',').map((x) => x.trim()).filter(Boolean)[0] ?? '';
    setPendingExplainability({
      business: business?.value ?? undefined,
      region: region?.value ?? undefined,
      fg: firstFg || undefined,
      fcd,
      asins: asinsInput || undefined,
    });
    setViewId('explainability');
  };

  // Metadata passed to the AI agent (Summarize / Chat) for the divergence view.
  const divergenceAgentMetadata: Record<string, string> = {
    view: 'forecast-divergence',
    business: business?.value ?? '',
    region: region?.value ?? '',
    fgids: fgidsInput,
    asins: asinsInput,
    fcd_start: dateRange?.type === 'absolute' ? dateRange.startDate : '',
    fcd_end: dateRange?.type === 'absolute' ? dateRange.endDate : '',
  };

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">View</Header>}>
        <Tiles
          value={viewId}
          onChange={({ detail }) => handleViewChange(detail.value)}
          columns={VIEWS.length}
          items={VIEWS.map((v) => ({
            value: v.id,
            label: v.label,
            disabled: !v.enabled,
            description: v.enabled ? undefined : 'Coming soon',
          }))}
        />
      </Container>

      {/* Both subtrees stay mounted; we toggle visibility so an in-flight job (and its
          polling) in either view survives a view switch. */}
      <div style={{ display: viewId === 'explainability' ? 'block' : 'none' }}>
        <ExplainabilityView prefill={pendingExplainability} />
      </div>
      <div style={{ display: viewId !== 'explainability' ? 'block' : 'none' }}>
      <SpaceBetween size="l">
      <Container header={<Header variant="h2">Parameters</Header>}>
        <form onSubmit={(e) => e.preventDefault()}>
          <Form>
            <SpaceBetween size="l">
              <ColumnLayout columns={5} minColumnWidth={180}>
                {has('business') && (
                  <FormField label="Business">
                    <Select
                      selectedOption={business}
                      onChange={({ detail }) => {
                        setBusiness(detail.selectedOption as Option);
                        setRegion(null);
                      }}
                      options={toOptions(businessOptions)}
                      placeholder="Select a business"
                    />
                  </FormField>
                )}
                {has('region') && (
                  <FormField label="Region">
                    <Select
                      selectedOption={region}
                      onChange={({ detail }) =>
                        setRegion(detail.selectedOption as Option)
                      }
                      options={regionOptions}
                      placeholder="Select a region"
                      disabled={!business}
                    />
                  </FormField>
                )}
                {has('fgids') && (
                  <FormField label="FGIDs">
                    <Input
                      value={fgidsInput}
                      onChange={({ detail }) => setFgidsInput(detail.value)}
                      placeholder="e.g. 100004, 100005"
                    />
                  </FormField>
                )}
                {has('siteIds') && (
                  <FormField label="Site IDs">
                    <Input
                      value={siteIdsInput}
                      onChange={({ detail }) => setSiteIdsInput(detail.value)}
                      placeholder="e.g. UMD1, UCA7"
                    />
                  </FormField>
                )}
                {has('asins') && (
                  <FormField label="ASINs">
                    <Input
                      value={asinsInput}
                      onChange={({ detail }) => setAsinsInput(detail.value)}
                      placeholder="e.g. B01MZHDHVM, B07XJ8C8F5"
                    />
                  </FormField>
                )}
              </ColumnLayout>

              {has('dateRange') && (
                <FormField label={view.viewType === 'divergence' ? 'FCD Range' : 'Date range'}>
                  <SpaceBetween size="xs">
                    <SpaceBetween direction="horizontal" size="xs">
                      {DATE_PRESETS.map((p) => (
                        <Button
                          key={p.days}
                          variant={dateMode === p.days ? 'primary' : 'normal'}
                          onClick={() => applyPreset(p.days)}
                        >
                          {p.label}
                        </Button>
                      ))}
                      <Button
                        variant={dateMode === 'custom' ? 'primary' : 'normal'}
                        onClick={() => setDateMode('custom')}
                      >
                        Custom
                      </Button>
                    </SpaceBetween>
                    {dateMode === 'custom' && (
                      <DateRangePicker
                        ref={pickerRef}
                        value={dateRange}
                        onChange={({ detail }) => setDateRange(detail.value)}
                        relativeOptions={[]}
                        rangeSelectorMode="absolute-only"
                        dateOnly
                        isValidRange={isValidRange}
                        i18nStrings={DATE_RANGE_I18N}
                        placeholder="Select a date range"
                      />
                    )}
                  </SpaceBetween>
                </FormField>
              )}

              <SpaceBetween direction="horizontal" size="xs">
                <span className="analyze-button">
                  <Button variant="primary" onClick={onSubmit} loading={isViewLoading}>
                    Analyze
                  </Button>
                </span>
                {isViewLoading && <Button onClick={onCancel}>Cancel</Button>}
                {rows != null && submitted && !isViewLoading && (
                  <Button iconName="refresh" onClick={onRefresh}>
                    Refresh
                  </Button>
                )}
                {hasActiveFilters && !isViewLoading && !submitted && (
                  <Button onClick={onClear}>Clear</Button>
                )}
              </SpaceBetween>
            </SpaceBetween>
          </Form>
        </form>
      </Container>

      {/* Only views with a backend recent-runs partition (demand/lifespan/divergence)
          render the shared panel. The explainability view has no recentRunsType — it
          manages its own ig/perturbation panels — so it's excluded by construction,
          and the type system (not a cast) guarantees we never send an invalid viewType. */}
      {view.recentRunsType && (
        // key by viewType so switching views fully REMOUNTS the panel. Without a
        // key React reuses one instance across view switches, so the panel keeps
        // the previous view's `runs` state (and its "Recent Runs (N)" header count)
        // until the new fetch resolves — the "loads as (0) then shows (4)" mismatch.
        // Remounting resets to empty + loading, so the header count never shows a
        // stale/other-view number.
        <RecentRunsApiPanel
          key={view.recentRunsType}
          viewType={view.recentRunsType}
          onLoadRun={loadRecentRun}
          refreshKey={divRunsKey}
        />
      )}

      {error && (
        <Alert type="error" header="Query failed">
          {error}
        </Alert>
      )}

      {rows && rows.length === 0 && !error && (
        <Alert type="info">No data found for the selected parameters.</Alert>
      )}

      {rows && rows.length > 0 && (
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {groupCount > 1 && (
                    <SegmentedControl
                      selectedId={granularity}
                      onChange={({ detail }) =>
                        setGranularity(detail.selectedId as Granularity)
                      }
                      options={[
                        { id: 'detailed', text: 'Detailed' },
                        { id: 'aggregated', text: 'Aggregated' },
                      ]}
                    />
                  )}
                  <SegmentedControl
                    selectedId={display}
                    onChange={({ detail }) =>
                      setDisplay(detail.selectedId as 'chart' | 'table')
                    }
                    options={[
                      { id: 'chart', text: 'Chart' },
                      { id: 'table', text: 'Table' },
                    ]}
                  />
                </SpaceBetween>
              }
            >
              {view.label}
            </Header>
          }
        >
          {display === 'chart' && view.chart ? (
            view.viewType === 'divergence' && divergenceResult ? (
              <DivergenceChart
                rows={divergenceResult.rows}
                divergencePoints={divergenceResult.divergencePoints}
                topDivergentTargetDates={divergenceResult.topDivergentTargetDates}
                onInvestigate={handleInvestigateFcd}
              />
            ) : (
              <ResultChart
                rows={rows ?? []}
                spec={view.chart}
                viewType={view.viewType}
                business={business?.value}
                region={region?.value}
              />
            )
          ) : (
            <ResultsTable
              rows={rows ?? []}
              fileName={csvFileName}
              groupColumn={view.chart?.groupColumn}
              groupMatch={view.chart?.groupMatch}
            />
          )}
        </Container>
      )}

      {view.viewType === 'divergence' && divergenceResult && divergenceResult.rows.length > 0 && (
        <>
          <AgentExplanationInline
            results={divergenceResult.rows}
            metadata={divergenceAgentMetadata}
          />
          <AgentChat
            results={divergenceResult.rows}
            metadata={divergenceAgentMetadata}
          />
        </>
      )}
      </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
